import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
} from 'react';

export type ThemeMode = 'system' | 'light' | 'dark';

interface ThemeContextType {
  theme: ThemeMode;
  resolvedTheme: 'light' | 'dark';
  setTheme: (newTheme: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

const STORAGE_KEY = 'admin_portal_theme';

const DAISY_THEMES = {
  light: 'repro-light',
  dark: 'repro-dark',
} as const;

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<ThemeMode>('system');
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>('light');

  const media = window.matchMedia('(prefers-color-scheme: dark)');

  const getSystemTheme = useCallback((): 'light' | 'dark' => {
    return media.matches ? 'dark' : 'light';
  }, [media.matches]);

  const applyTheme = useCallback((resolved: 'light' | 'dark') => {
    if (typeof document !== 'undefined') {
      // DaisyUI uses data-theme for theme switching
      document.documentElement.dataset.theme = DAISY_THEMES[resolved];

      if (resolved === 'dark') {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }
  }, []);

  const resolveTheme = useCallback(
    (themeMode: ThemeMode): 'light' | 'dark' => {
      if (themeMode === 'system') {
        return getSystemTheme();
      }
      return themeMode;
    },
    [getSystemTheme]
  );

  useEffect(() => {
    const savedTheme = localStorage.getItem(STORAGE_KEY) as ThemeMode;
    const initialTheme =
      savedTheme && ['system', 'light', 'dark'].includes(savedTheme) ? savedTheme : 'system';

    setThemeState(initialTheme);
    const resolved = resolveTheme(initialTheme);
    setResolvedTheme(resolved);
    applyTheme(resolved);
  }, [resolveTheme, applyTheme]);

  useEffect(() => {
    if (theme !== 'system') return;

    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
      const systemTheme = e.matches ? 'dark' : 'light';
      setResolvedTheme(systemTheme);
      applyTheme(systemTheme);
    };

    media.addEventListener('change', handleSystemThemeChange);

    const systemTheme = getSystemTheme();
    setResolvedTheme(systemTheme);
    applyTheme(systemTheme);

    return () => {
      media.removeEventListener('change', handleSystemThemeChange);
    };
  }, [theme, getSystemTheme, applyTheme, media]);

  useEffect(() => {
    const resolved = resolveTheme(theme);
    setResolvedTheme(resolved);
    applyTheme(resolved);
  }, [theme, resolveTheme, applyTheme]);

  const setTheme = (newTheme: ThemeMode) => {
    setThemeState(newTheme);
    localStorage.setItem(STORAGE_KEY, newTheme);
  };

  const value: ThemeContextType = {
    theme,
    resolvedTheme,
    setTheme,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
